import React, { useEffect, useMemo, useState } from 'react';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const stageLabels = { qualification: 'تصفيات', final: 'نهائي' };
const categoryLabels = { rifle: 'بندقية', pistol: 'مسدس', shotgun: 'رماية تراب/سكيت' };
const categoryColors = { rifle: '#dc2626', pistol: '#2563eb', shotgun: '#16a34a' };

const emptyForm = { participant_id: '', discipline_id: '', season_year: 2026, stage: 'qualification', score: '', inner_ten: '', rank: '', is_record: 'none', notes: '' };

export default function Results() {
  const { user } = useAuth();
  const canEdit = user.role === 'admin' || user.role === 'reviewer';

  const [stats, setStats] = useState(null);
  const [disciplines, setDisciplines] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [filterDiscipline, setFilterDiscipline] = useState('');
  const [search, setSearch] = useState('');

  const [form, setForm] = useState(emptyForm);
  const [participantQuery, setParticipantQuery] = useState('');
  const [participantOptions, setParticipantOptions] = useState([]);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);

  const loadStats = () => client.get('/results/stats').then(r => setStats(r.data));
  const loadDisciplines = () => client.get('/disciplines').then(r => setDisciplines(r.data));
  const loadResults = () => client.get('/results', { params: { discipline_id: filterDiscipline, search } })
    .then(r => { setRows(r.data.data); setTotal(r.data.total); });

  useEffect(() => { loadStats(); loadDisciplines(); }, []);
  useEffect(() => { loadResults(); /* eslint-disable-next-line */ }, [filterDiscipline]);

  const searchParticipants = async (q) => {
    setParticipantQuery(q);
    if (q.length < 2) { setParticipantOptions([]); return; }
    const { data } = await client.get('/participants', { params: { search: q, limit: 8 } });
    setParticipantOptions(data.data);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setMsg(null);
    try {
      await client.post('/results', form);
      setMsg('تم تسجيل النتيجة بنجاح');
      setForm(emptyForm);
      setParticipantQuery('');
      loadStats(); loadResults();
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء حفظ النتيجة');
    }
  };

  const doSearch = (e) => { e.preventDefault(); loadResults(); };

  const chartData = useMemo(() => {
    if (!stats) return [];
    return stats.perDiscipline.map(d => ({ name: d.code, avg: Number(d.avg_qualification) || 0, category: d.category }));
  }, [stats]);

  return (
    <div>
      <div className="grid grid-4" style={{ marginBottom: 20 }}>
        <div className="card stat-card">
          <div className="stat-num">{stats?.total_results ?? '—'}</div>
          <div className="stat-label">إجمالي النتائج المسجلة</div>
        </div>
        <div className="card stat-card">
          <div className="stat-num">{stats?.participants_with_results ?? '—'}</div>
          <div className="stat-label">عدد المشاركين لديهم نتائج</div>
        </div>
        <div className="card stat-card seal">
          <div className="stat-num">{stats?.records_count ?? '—'}</div>
          <div className="stat-label">أرقام قياسية مسجلة</div>
        </div>
        <div className="card stat-card">
          <div className="stat-num">12</div>
          <div className="stat-label">مسابقات أولمبية للرماية</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>متوسط نتيجة التصفيات لكل مسابقة</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData}>
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis />
            <Tooltip />
            <Bar dataKey="avg" radius={[6, 6, 0, 0]}>
              {chartData.map((entry, i) => <Cell key={i} fill={categoryColors[entry.category]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>أفضل نتيجة لكل مسابقة</h3>
        <table>
          <thead><tr><th>المسابقة</th><th>النوع</th><th>أفضل نتيجة (تصفيات)</th><th>صاحب الرقم</th><th>عدد النتائج المسجلة</th></tr></thead>
          <tbody>
            {stats?.perDiscipline.map(d => (
              <tr key={d.id}>
                <td>
                  <span className="badge" style={{ background: categoryColors[d.category] }}>{categoryLabels[d.category]}</span>
                  {' '}{d.name_ar}
                </td>
                <td>{d.gender === 'male' ? 'رجال' : 'سيدات'}</td>
                <td className="mono">{d.best_qualification ?? '—'} / {d.qualification_max}</td>
                <td>{d.best_holder || '—'}</td>
                <td>{d.result_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canEdit && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginTop: 0 }}>تسجيل نتيجة جديدة</h3>
          {msg && <div className="alert alert-success">{msg}</div>}
          {error && <div className="alert alert-error">{error}</div>}
          <form onSubmit={submit}>
            <div className="form-grid">
              <div className="form-field" style={{ position: 'relative' }}>
                <label>المشارك (اكتب للبحث) *</label>
                <input
                  value={participantQuery}
                  onChange={e => searchParticipants(e.target.value)}
                  placeholder="اسم اللاعب..."
                  required={!form.participant_id}
                />
                {participantOptions.length > 0 && (
                  <div className="card" style={{ position: 'absolute', zIndex: 5, width: '100%', padding: 6, marginTop: 4 }}>
                    {participantOptions.map(p => (
                      <div
                        key={p.id}
                        style={{ padding: '6px 8px', cursor: 'pointer', fontSize: 13 }}
                        onClick={() => { setForm({ ...form, participant_id: p.id }); setParticipantQuery(p.full_name_ar); setParticipantOptions([]); }}
                      >
                        {p.full_name_ar} — {p.category_name_ar}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-field">
                <label>المسابقة *</label>
                <select value={form.discipline_id} onChange={e => setForm({ ...form, discipline_id: e.target.value })} required>
                  <option value="">اختر المسابقة</option>
                  {disciplines.map(d => <option key={d.id} value={d.id}>{d.name_ar}</option>)}
                </select>
              </div>

              <div className="form-field">
                <label>الموسم</label>
                <input type="number" value={form.season_year} onChange={e => setForm({ ...form, season_year: e.target.value })} />
              </div>
              <div className="form-field">
                <label>المرحلة</label>
                <select value={form.stage} onChange={e => setForm({ ...form, stage: e.target.value })}>
                  <option value="qualification">تصفيات</option>
                  <option value="final">نهائي</option>
                </select>
              </div>
              <div className="form-field">
                <label>النتيجة *</label>
                <input type="number" step="0.1" value={form.score} onChange={e => setForm({ ...form, score: e.target.value })} required />
              </div>
              <div className="form-field">
                <label>عدد الوسط (Inner Ten)</label>
                <input type="number" value={form.inner_ten} onChange={e => setForm({ ...form, inner_ten: e.target.value })} />
              </div>
              <div className="form-field">
                <label>الترتيب</label>
                <input type="number" value={form.rank} onChange={e => setForm({ ...form, rank: e.target.value })} />
              </div>
              <div className="form-field">
                <label>رقم قياسي؟</label>
                <select value={form.is_record} onChange={e => setForm({ ...form, is_record: e.target.value })}>
                  <option value="none">لا يوجد</option>
                  <option value="national">رقم قياسي وطني</option>
                  <option value="world">رقم قياسي عالمي</option>
                </select>
              </div>
            </div>
            <button className="btn btn-primary" style={{ marginTop: 16 }}>حفظ النتيجة</button>
          </form>
        </div>
      )}

      <div className="card">
        <form onSubmit={doSearch} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end', marginBottom: 14 }}>
          <div className="form-field" style={{ minWidth: 220 }}>
            <label>بحث باسم اللاعب</label>
            <input value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="form-field" style={{ minWidth: 220 }}>
            <label>تصفية حسب المسابقة</label>
            <select value={filterDiscipline} onChange={e => setFilterDiscipline(e.target.value)}>
              <option value="">كل المسابقات</option>
              {disciplines.map(d => <option key={d.id} value={d.id}>{d.name_ar}</option>)}
            </select>
          </div>
          <button className="btn btn-primary">بحث</button>
        </form>

        <h3 style={{ marginTop: 0 }}>النتائج ({total})</h3>
        <table>
          <thead>
            <tr>
              <th>اللاعب</th><th>الجهة</th><th>المسابقة</th><th>المرحلة</th><th>النتيجة</th><th>الترتيب</th><th>الموسم</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td>{r.full_name_ar} {r.is_record !== 'none' && <span title={r.is_record}>🏅</span>}</td>
                <td>{r.organization_name}</td>
                <td>{r.discipline_name_ar}</td>
                <td><span className="badge" style={{ background: r.stage === 'final' ? '#6d28d9' : '#9ca3af' }}>{stageLabels[r.stage]}</span></td>
                <td className="mono">{r.score}</td>
                <td>{r.rank ?? '—'}</td>
                <td>{r.season_year}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan="7" style={{ textAlign: 'center', color: '#9ca3af' }}>لا توجد نتائج مطابقة</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
